/* eslint-disable react-perf/jsx-no-new-function-as-prop -- React Hook Form Controller requires inline render function */
import {
  TEST_ID_CONFIRM_CODE_ERROR,
  TEST_ID_CONFIRM_CODE_ERROR_TEXT,
  TEST_ID_CONFIRM_CODE_INPUT_CODE,
} from "e2e/testId";
import { AlertCircleIcon } from "lucide-react-native";
import React, { memo } from "react";
import { Control, Controller, FieldErrors } from "react-hook-form";
// design-system: rn-primitive waiver — dynamic style / behavior-coupled
import {
  NativeSyntheticEvent,
  // eslint-disable-next-line no-restricted-imports -- RFC-#6 compound-button waiver: Button/ButtonText with a custom data-driven background (bg-surface-subtle / bg-surface-muted by isDark) and raw-palette label tones…
  TextInput,
  TextInputSelectionChangeEventData,
  // eslint-disable-next-line no-restricted-imports -- RFC-#6 compound-button waiver: Button/ButtonText with a custom data-driven background (bg-surface-subtle / bg-surface-muted by isDark) and raw-palette label tones…
  TouchableWithoutFeedback,
  // eslint-disable-next-line no-restricted-imports -- RFC-#6 compound-button waiver: Button/ButtonText with a custom data-driven background (bg-surface-subtle / bg-surface-muted by isDark) and raw-palette label tones…
  View,
} from "react-native";

import Screen from "@/features/auth/components/organisms/Screen";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
// design-system: compound waiver — non-collapsible button composition
// eslint-disable-next-line no-restricted-imports -- RFC-#6 compound-button waiver: Button/ButtonText with a custom data-driven background (bg-surface-subtle / bg-surface-muted by isDark) and raw-palette label tones…
import { Button, ButtonText } from "@/components/ui/button";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from "@/components/atoms/FormControl";
import { ifWeb } from "@/utils/platform";

/**
 *
 */
export interface IForm {
  code: string;
}

/**
 *
 */
interface Props {
  control: Control<IForm>;
  errors: FieldErrors<IForm>;
  valid: boolean;
  onSubmit: () => void;
  loading: boolean;
  onPress: () => void;
  onChange: (text: string) => void;
  onSelectionChange: (
    e: NativeSyntheticEvent<TextInputSelectionChangeEventData>
  ) => void;
  inputRef: React.RefObject<TextInput>;
  widthContent: number;
  boxArray: number[];
  boxDigit: (digit: number, index: number) => React.ReactNode;
  onResendOTP: () => void;
  isDark: boolean;
}

/**
 * View component for confirmation code entry screen with 6-digit code input
 * @param root0 - Component props
 * @param root0.control - React Hook Form control object for managing form state
 * @param root0.errors - React Hook Form errors object containing validation error messages
 * @param root0.valid - Boolean indicating whether the confirmation code form is valid
 * @param root0.onSubmit - Callback fired when the verify button is pressed to submit the code
 * @param root0.loading - Boolean indicating verification is in progress
 * @param root0.onPress - Callback fired when the input area is pressed to focus the hidden input
 * @param root0.onChange - Callback fired when the code input text changes
 * @param root0.onSelectionChange - Callback fired when text selection changes in the input
 * @param root0.inputRef - React ref object for the hidden TextInput element
 * @param root0.boxArray - Array of numbers used to render the individual digit boxes
 * @param root0.widthContent - Width value for the content container in pixels
 * @param root0.boxDigit - Function to render each individual digit box in the code input
 * @param root0.onResendOTP - Callback fired when the resend OTP button is pressed
 * @param root0.isDark - Boolean indicating dark theme is active for conditional styling
 */
const ConfirmCodeView = ({
  control,
  errors,
  valid,
  onSubmit,
  loading,
  onPress,
  onChange: passedOnChange,
  onSelectionChange,
  inputRef,
  boxArray,
  widthContent,
  boxDigit,
  onResendOTP,
  isDark,
}: Props) => (
  <Screen
    title="Enter Code"
    subTitle="Please enter the 6-digit code sent to your phone."
    backToSignIn
    widthContent={widthContent}
    isDisableButtonBack={loading}
    footer={
      <Stack className="gap-4">
        <Button
          isDisabled={loading || !valid}
          onPress={onSubmit}
          className={isDark ? "bg-surface-subtle" : "bg-surface-muted"}
          size="xl"
        >
          <ButtonText
            className={`font-body ${ifWeb("text-title", "")} ${!loading && valid ? "text-status-info" : "text-content-muted"}`}
          >
            {!loading ? "Verify" : "Please Wait..."}
          </ButtonText>
        </Button>
        <Button
          isDisabled={loading}
          variant="link"
          className="self-center"
          onPress={onResendOTP}
        >
          <ButtonText className="text-status-info">Resend OTP</ButtonText>
        </Button>
      </Stack>
    }
  >
    <Controller
      name="code"
      control={control}
      render={({ field: { value, onChange } }) => (
        <FormControl
          size="md"
          isDisabled={false}
          isInvalid={!loading && !!errors.code}
          isReadOnly={false}
          isRequired={false}
          className="px-4"
        >
          <TouchableWithoutFeedback onPress={onPress}>
            <View className="flex-row justify-center">
              <TextInput
                value={value}
                testID={TEST_ID_CONFIRM_CODE_INPUT_CODE}
                onChangeText={text => {
                  onChange(text);
                  passedOnChange(text);
                }}
                keyboardType="numeric"
                textContentType="oneTimeCode"
                autoComplete="sms-otp"
                maxLength={6}
                className="absolute z-10 size-full opacity-0"
                caretHidden
                selectTextOnFocus={false}
                ref={inputRef}
                onFocus={onPress}
                onSelectionChange={onSelectionChange}
              />
              {/* SE-5549: the six digit cells flex to share the row width
                  (capped at 56px each) so they never overflow the screen
                  margins on phones — fixed size-14 cells summed to ~396px,
                  wider than a 360dp device's content column. */}
              <Box className="w-full flex-row justify-center gap-2 sm:gap-3">
                {/* Wrap the render fn so `.map` only passes (digit, index) —
                    not the array as a 3rd arg (Sonar typescript:S7727). */}
                {boxArray.map((digit, index) => boxDigit(digit, index))}
              </Box>
            </View>
          </TouchableWithoutFeedback>
          {/* SE-6887: the DURABLE rejection signal. FormControlError renders
              `null` unless the enclosing FormControl is invalid AND it has
              children, so the presence of this region is itself the assertion
              — no timer takes it away and no copy has to be matched. The
              container sets the error from the confirm-sign-in OUTCOME, so the
              region appears for every rejection branch, including messages the
              client has never seen. */}
          <FormControlError testID={TEST_ID_CONFIRM_CODE_ERROR}>
            <FormControlErrorIcon
              as={AlertCircleIcon}
              {...ifWeb({ size: "md" }, {})}
            />
            {/* SE-6773: the web arm used to name an `md` size step, for which
                this theme has no fontSize token — so it compiled to nothing.
                FormControlErrorText takes its size from the enclosing
                FormControl (size "md" above -> formControlErrorTextStyle md ->
                text-title-sm), which is what the web arm already rendered. The
                native arm carries no size class and is unchanged. */}
            <FormControlErrorText
              testID={TEST_ID_CONFIRM_CODE_ERROR_TEXT}
              accessibilityRole="alert"
              accessibilityLiveRegion="assertive"
              className={ifWeb(
                "shrink pl-1 text-title-sm self-center",
                "shrink pl-1"
              )}
            >
              {errors.code?.message}
            </FormControlErrorText>
          </FormControlError>
        </FormControl>
      )}
    />
  </Screen>
);

ConfirmCodeView.displayName = "ConfirmCodeView";

export default memo(ConfirmCodeView);
/* eslint-enable react-perf/jsx-no-new-function-as-prop -- Re-enable after component definition */
